<template>
  <div
    :class="classes"
    @mouseenter="handleHover(true)"
    @mouseleave="handleHover(false)"
    v-if="!kicked"
  >
    <UserIcon class="user-result__icon" :userId="data.userId" :userStatus="status" />
    <div class="user-result-content">
      <div class="user-result-content__name">{{ data.fullName }} <span class="pronouns">{{data.pronouns ? `(${data.pronouns})` : ''}}</span></div>
      <div class="user-result-content__field">{{ data.field1 }}</div>
      <div class="user-result-content__field">{{ data.field2 }}</div>
      <div v-if="hasIcons" class="user-result-content__icons">
        <a v-if="linkedIn" class="user-result-content__icon" :href="linkedIn" target="_blank" rel="noopener noreferrer" :aria-label="`${data.fullName}'s LinkedIn`">
          <img src="@/assets/global-icons/linkedin.svg" :alt="`${data.fullName}'s LinkedIn`"/>
        </a>
      </div>
      <div class="user-result-content-status">
        <div
          v-if="statusText"
          :class="`user-result-content-status__text ${ifMe || (data.invited && !data.joined) ? '' : status} ${
            data.invited ? 'invited' : ''
          }`"
        >
          {{ statusText }}
          <!-- I was going to use v-html to do the below, but that would introduce an XSS vuln, since room names are user inputted -->
          <template v-if="!alternateStatusTextActive && status === 'unavailable' && searchUsersCurrentRoomName">
            in <u>{{ searchUsersCurrentRoomName }}</u>
          </template>
        </div>
        <div class="user-result-content-status__buttons">
          <button
            v-if="ifInviteAllowed"
            class="user-result-content-status__button"
            @click="inviteUser"
            @mouseenter="hoveringInvite = true"
            @mouseleave="hoveringInvite = false"
            role="button"
            :aria-label="inviteText"
          >
          <img src="@/assets/notification-icons/camcorder-green.svg" :alt="inviteText" aria-hidden="true"/>
          </button>
          <PinButton
            v-if="ifPinningAllowed"
            @mouseenter="hoveringPin = true"
            @mouseleave="hoveringPin = false"
            @onPinChanged="p => pinned = p"
            :initialPinId="data.pinsId"
            :userId="data.userId"
          />
        </div>
      </div>
      <div class="footer-buttons" v-if="isHost && footerButtons.length">
        <btn narrow small v-for="button in footerButtons" :key="button.label" :colour="button.colour" @click="button.click"><template #text>{{button.label}}</template></btn>
      </div>
    </div>
    <GenericMenu v-if="menuItems.length" class="user-result__menu" :items="menuItems"/>
  </div>
</template>

<script>
import GenericMenu from '@/components/menu/GenericMenu.vue';
import UserIcon from '../UserIcon';
import { mapActions, mapMutations, mapGetters } from 'vuex';
import PinButton from '@/components/atoms/PinButton.vue';

const statusTextMap = {
  available: 'Available',
  doNotDisturb: 'Not available',
  offline: 'Offline',
  elsewhere: 'In a different Space',
  unavailable: 'Busy'
};

export default {
  name: 'SearchResultUser',
  components: { UserIcon, PinButton, GenericMenu },
  data: function () {
    return {
      inviteClicked: false,
      hoveringInvite: false,
      hoveringPin: false,
      kicked: false,
      pinned: this.data.pinsId != null
    };
  },
  props: {
    type: String,
    data: Object,
    showStatus: { type: Boolean, default: true },
    isSpaceAdmin: { type: Boolean, default: false },
    isInJumpSpace: { type: Boolean, default: false },
    spaceInfo: { type: Object, default: null }
  },
  computed: {
    ...mapGetters({
      usersCurrentRoomIsHub: 'getIfCurrentUsersRoomIsHub',
      usersCurrentRoomName: 'getCurrentUsersRoomName',
      usersCurrentRoomId: 'getCurrentUsersRoomId',
      currentWhereaboutsData: 'getCurrentWhereaboutsData',
      currentRoomPageId: 'getSpacePageId',
      currentRoomPageName: 'getSpaceName',
      hubId: 'getCurrentAreaHubId',
      isAllowed: 'permissions/isAllowed'
    }),
    classes() {
      return {
        'user-result': true,
        [this.status]: true,
        'invited': this.data.invited && !this.data.joined && !this.isInLobby,
        'user-result--has-menu': this.menuItems.length
      }
    },
    status() {
      if(this.data.status === 'elsewhere' && this.ifInviteAllowed) return 'available' //technically available in another area
      else return this.data.status
    },
    inviteSent: (vm) => vm.inviteClicked || vm.data.invited,
    ifMe: (vm) => vm.currentWhereaboutsData?.userId === vm.data.userId,
    searchUsersCurrentRoomName: (vm) => (vm.data.whereabouts ? vm.data.whereabouts?.space?.name : null),
    // todo: move this into usericon?
    avatar: ({ data: { avatar } }) => (avatar ? { avatar } : { placeholder: require('@/assets/images/participant.png') }),
    ifInviteAllowed() {
      return this.data.requestToJoin && !this.inviteSent;
    },
    ifPinningAllowed() {
      return !this.ifMe && this.status != 'available' && !this.inviteSent;
    },
    statusText() {
      if (this.ifMe) return 'Me';
      if (this.inviteSent && !this.data.joined) {
        let invitedTo = 'a room'
        if(this.type === 'room-info' && this.currentRoomPageId === this.usersCurrentRoomId) {
          invitedTo = this.usersCurrentRoomName
        } else if(this.type === 'room-info') {
          invitedTo = this.currentRoomPageName
        } else {
          invitedTo = this.usersCurrentRoomIsHub ? 'your Room' : this.usersCurrentRoomName
        }
        return `Invited to ${invitedTo}`
      }
      else if (this.hoveringPin) return this.pinText;
      else if (!this.showStatus) return ''
      else if (this.data.status === 'elsewhere' && this.status === 'available') return 'Available in another Space'
      // else if(this.inviteSent || (!this.data.requestToJoin && (this.data.status === 'available' || this.data.status === 'doNotDisturb'))) return "Invite sent";
      else return statusTextMap[this.status];
    },
    inAnotherSpace() {
      return this.data.status === 'elsewhere'
    },
    inviteText() {
      return 'Invite to ' + (this.usersCurrentRoomIsHub ? 'your Room' : this.usersCurrentRoomName);
    },
    pinText() {
      return this.pinned ? 'Unpin user' : 'Pin user';
    },
    isHost() {
      return this.isAllowed('room.lobby.acceptOrDeny') //NEEDS EXTENDING - waiting on API
    },
    isInLobby() {
      return this.data.inLobby && !this.data.joined
    },
    isInHub() {
      return this.data.whereabouts?.space?.spaceType === "hub"
    },
    footerButtons() {
      let buttons = []

      if (!this.isMe) {
        if (this.isInLobby) {
          buttons = [
            {
              label: 'Decline',
              colour: 'red',
              click: () => this.handleRejectRoomLobbyGuest([this.currentRoomPageId, this.data.userId])
            },
            {
              label: 'Accept',
              colour: 'green',
              click: () => this.handleAcceptRoomLobbyGuest([this.currentRoomPageId, this.data.userId])
            }
          ]
        }
      }

      return buttons
    },
    menuItems() {
      let items = []

      if (!this.ifMe && !this.isInLobby) {
        if (this.isAllowed("room.makeCoHost") && !this.isInHub) {
          items.push({
            icon: 'cohost',
            text: 'Make a co-host',
            click: () => this.makeCohost()
          })
        }

        if (this.isAllowed("room.kickOut") && !this.isInHub) {
          items.push({
            icon: 'delete',
            text: 'Kick Out',
            click: () => this.kickOut()
          })
        }
      }

      return items
    },
    // checks if any status text that isn't in statusTextMap is being used
    alternateStatusTextActive: (vm) => vm.ifMe || vm.hoveringInvite || vm.hoveringPin || (vm.inviteSent && !vm.data.joined),
    linkedIn: (vm) => {
      if (vm.data.linkedInProfileUrl && vm.data.linkedInProfileUrl.length) return vm.data.linkedInProfileUrl
    },
    hasIcons: (vm) => vm.linkedIn
  },
  methods: {
    ...mapActions(['inviteUserToSpaceHandler', 'inviteUserToJumpSpaceHandler', 'popups/nav/confirmMakeCohost', 'handleAcceptRoomLobbyGuest', 'handleRejectRoomLobbyGuest', 'handleKickFromRoom', 'handleMakeCohost']),
    ...mapMutations(['search/HIGHLIGHTED_ROOM', 'search/HIGHLIGHTED_USER']),
    inviteUser() {
      this.inviteClicked = true;
      if(this.isInJumpSpace) this.inviteUserToJumpSpaceHandler([this.data, this.spaceInfo])
      else this.inviteUserToSpaceHandler([this.data, this.inAnotherSpace]);
    },
    makeCohost() {
      const roomId = this.currentRoomPageId

      this['popups/nav/confirmMakeCohost']({
        name: this.data.fullName,
        callback: () => this.handleMakeCohost([roomId, this.data.userId])
      })
    },
    async kickOut() {
      const roomId = this.currentRoomPageId
      const res = await this.handleKickFromRoom([roomId, this.data.userId])

      if (res.success) {
        // hide this from the participants list until it refreshes
        this.kicked = true
      }
    },
    handleHover(on) {
      if (!this.showStatus) return;
      if (on && !this.currentRoomPageId) {
        //
        this['search/HIGHLIGHTED_ROOM'](this.data.whereabouts?.space?.id);
        this['search/HIGHLIGHTED_USER'](this.data.userId);
      } else {
        this['search/HIGHLIGHTED_ROOM']('');
        this['search/HIGHLIGHTED_USER']('');
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import '~@/scss/components/search/search_result_user.scss';
</style>
